import { LazoPaths } from "@/enums/LazoPaths";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { AddOnObjectKeyType } from "@/models/subscriptions/AddOn";
import { isPaidSubscription } from "@/models/subscriptions/Subscription";
import { SubscriptionPurchase } from "@/models/subscriptions/SubscriptionPurchase";
import ProductFlowService from "@/services/ProductFlowService";
import SubscriptionPurchaseService from "@/services/subscriptions/SubscriptionPurchaseService";
import { AccessMapType, generateAccessMap } from "@/utils/RedirectUtils";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { useTenantMemberships } from "./TenantContext";

const isDataRoomPath = (path: string): boolean =>
  path.startsWith(LazoPaths.ONE_DATA_ROOM) || path.startsWith(LazoPaths.ONE_VIRTUAL_DATA_ROOMS);

const redirectIfNeeded = (c: SubscriptionPurchase, tenantExternalId: string, navigate: NavigateFunction, isExternalUser: boolean) => {
  const currentPath = window.location.pathname;
  const deniedCurrentPath = generateAccessMap(c)[currentPath] === false;
  const shouldSkipPaywallForExternalDataRoom = isExternalUser && isDataRoomPath(currentPath);

  // Check if we are in a path that is available with the current addons
  if (deniedCurrentPath && !shouldSkipPaywallForExternalDataRoom) {
    // If its not, check if it isn't solo, if it isn't and has an active
    // product flow redirect to tasks page else to fundraising
    if (!c.subscription.objectKey.includes("solo") && tenantExternalId) {
      ProductFlowService.hasActive(tenantExternalId).then((r) => {
        r.onSuccess((hasActiveFlow) => {
          if (hasActiveFlow) {
            navigate(LazoPaths.TASKS);
          } else {
            navigate(LazoPaths.ONE_VIRTUAL_DATA_ROOMS);
          }
        });
      });
    }

    // If its solo just redirect to shared spaces
    navigate(LazoPaths.ONE_VIRTUAL_DATA_ROOMS);
  }
};

export const isPaidSubscriptionPurchase = (purchase: SubscriptionPurchase): boolean => {
  return isPaidSubscription(purchase.subscription);
};

type SubscriptionPurchaseProviderType = {
  subscriptionPurchase: SubscriptionPurchase;
  loadingSubscription: boolean;
  hasSmartListsCreation: boolean;
  hasAccounting: boolean;
  accessMap: AccessMapType;
  triggerFetch: (redirect: boolean) => void;
  updatePurchase: (c: SubscriptionPurchase) => void;
};

const subscriptionPurchaseContext = createContext<SubscriptionPurchaseProviderType | undefined>(undefined);

export const SubscriptionPurchaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscriptionPurchase, setSubscriptionPurchase] = useState<SubscriptionPurchase>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasSmartListsCreation, setHasSmartListsCreation] = useState<boolean>(false);
  const [hasAccounting, setHasAccounting] = useState<boolean>(false);

  /**
   * Access map, use it with the currenth pathname (window.location.pathname),
   * if the value is false (and only false)
   * the user has no access, if its undefined we don't know.
   */
  const [accessMap, setAccessMap] = useState<AccessMapType>({});

  const { currentTenantMembership, workspaceState } = useTenantMemberships();
  const navigate = useNavigate();

  function updatePurchase(c: SubscriptionPurchase) {
    c.subscription.objectKey = c.subscription.objectKey.replace(/-old-version-\d*/, "");

    c.subscription.addOns = c.subscription.addOns.map((a) => {
      a.objectKey = a.objectKey.replace(/-old-version-\d*/, "");
      return a;
    });

    c.extraAddOns = c.extraAddOns.map((a) => {
      a.objectKey = a.objectKey.replace(/-old-version-\d*/, "");
      return a;
    });

    setHasSmartListsCreation(
      c.subscription.addOns.some(
        (addon) => addon.objectKey === AddOnObjectKeyType.SMARTLISTS || addon.objectKey === AddOnObjectKeyType.SMARTLISTS_UP_TO_ONE
      ) ||
        c.extraAddOns.some(
          (addon) => addon.objectKey === AddOnObjectKeyType.SMARTLISTS || addon.objectKey === AddOnObjectKeyType.SMARTLISTS_UP_TO_ONE
        )
    );
    setHasAccounting(
      c.subscription.addOns.some((addon) => addon.objectKey === AddOnObjectKeyType.ACCOUNTING_MODULE) ||
        c.extraAddOns.some((addon) => addon.objectKey === AddOnObjectKeyType.ACCOUNTING_MODULE)
    );

    setSubscriptionPurchase(c);
    setAccessMap(generateAccessMap(c));
  }

  const triggerFetch = useCallback(
    (redirect: boolean) => {
      if (!currentTenantMembership) return;
      setLoading(true);
      SubscriptionPurchaseService.getActiveByUserAndTenant(currentTenantMembership?.tenant.externalId)
        .then((r) => {
          r.onSuccess((c) => {
            updatePurchase(c);
            const isExternalUser = !workspaceState?.isLazoAdminOrKam;
            redirect ? redirectIfNeeded(c, currentTenantMembership?.tenant?.externalId, navigate, isExternalUser) : () => {};
          });
          r.onError((e) => {
            NotificationService.error(e);
            setSubscriptionPurchase(null);
          });
        })
        .finally(() => setLoading(false));
    },
    [currentTenantMembership?.tenant?.externalId, workspaceState?.isLazoAdminOrKam]
  );

  useEffect(() => {
    if (!currentTenantMembership || !currentTenantMembership.tenant) return;
    triggerFetch(true);
  }, [currentTenantMembership]);

  const contextValue = useMemo(() => {
    return {
      subscriptionPurchase,
      loadingSubscription: loading,
      hasSmartListsCreation,
      hasAccounting,
      accessMap,
      triggerFetch,
      updatePurchase,
    };
  }, [subscriptionPurchase, loading, hasSmartListsCreation, hasAccounting, accessMap, triggerFetch]);

  return <subscriptionPurchaseContext.Provider value={contextValue}>{children}</subscriptionPurchaseContext.Provider>;
};

export const useSubscriptionPurchase = (): SubscriptionPurchaseProviderType => {
  const ctx = useContext(subscriptionPurchaseContext);
  return ctx;
};
